import React, { useEffect, useMemo, useState } from "react";

// Toddler-friendly Match Game (no Tailwind, pure CSS) — flow layout (no scrolling gaps)

const ALL_ITEMS = [
  { id: "dog", label: "dog", emoji: "🐶" },
  { id: "cat", label: "cat", emoji: "🐱" },
  { id: "duck", label: "duck", emoji: "🦆" },
  { id: "bunny", label: "bunny", emoji: "🐰" },
  { id: "fish", label: "fish", emoji: "🐟" },
  { id: "apple", label: "apple", emoji: "🍎" },
  { id: "ball", label: "ball", emoji: "⚽" },
  { id: "car", label: "car", emoji: "🚗" },
  { id: "star", label: "star", emoji: "⭐" },
  { id: "bus", label: "bus", emoji: "🚌" },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
const sampleWithout = (arr, n, excludeId) => {
  const filtered = excludeId ? arr.filter((x) => x.id !== excludeId) : [...arr];
  return shuffle(filtered).slice(0, Math.max(0, Math.min(n, filtered.length)));
};

export default function ToddlerMatchGame() {
  const [optionsCount, setOptionsCount] = useState(4);
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);
  const [phase, setPhase] = useState("intro"); // intro -> find -> feedback
  const [round, setRound] = useState(1);
  const [stars, setStars] = useState(0);
  const [wrongPickId, setWrongPickId] = useState(null);
  const [flashTarget, setFlashTarget] = useState(false);

  const startRound = React.useCallback(() => {
    const newTarget = pickRandom(ALL_ITEMS);
    const others = sampleWithout(ALL_ITEMS, optionsCount - 1, newTarget.id);
    const opts = shuffle([newTarget, ...others]);

    setTarget(newTarget);
    setOptions(opts);
    setWrongPickId(null);
    setPhase("intro");
    const t = setTimeout(() => setPhase("find"), 1400);
    return () => clearTimeout(t);
  }, [optionsCount]);

  useEffect(() => { startRound(); }, [startRound]);

  // Lock body scroll when overlay is visible
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = phase === "feedback" ? "hidden" : "";
  }, [phase]);

  function handlePick(item) {
    if (phase !== "find") return;
    if (item.id === target.id) {
      setStars((s) => s + 1);
      setPhase("feedback");
      setTimeout(() => {
        setRound((r) => r + 1);
        startRound();
      }, 1100);
    } else {
      setWrongPickId(item.id);
      try { if (navigator?.vibrate) navigator.vibrate(120); } catch {}
      setTimeout(() => setWrongPickId(null), 400);
    }
  }

  function showAgain() {
    setFlashTarget(true);
    setTimeout(() => setFlashTarget(false), 550);
  }

  return (
    <div className="tm-root">
      <style>{`
        :root{
          --bg1:#f7faff; --bg2:#ffffff;
          --ink:#0f172a; --muted:#5b6474; --card:#ffffff; --ring:#c7e2ff; --border:#e6e9ee;
          --shadow:0 8px 24px rgba(16,24,40,.06);
          --radius-xl:24px; --radius-2xl:28px;
        }

        /* === Layout: FLOW (no vertical centering) === */
        .tm-root{
          width:100%;
          padding:24px 0;               /* small vertical breathing room */
          background:linear-gradient(180deg,var(--bg1),var(--bg2));
          color:var(--ink);
          font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,"Helvetica Neue",Arial;
        }
        .tm-shell{ width:100%; max-width:820px; margin:0 auto; padding:0 16px; }

        .tm-row{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .tm-stars{ display:flex; align-items:center; gap:8px; color:#f59e0b; user-select:none; }
        .tm-round{ color:var(--muted); font-size:14px; }

        .tm-target{
          background:var(--card); border:1px solid var(--border);
          border-radius:var(--radius-2xl); box-shadow:var(--shadow);
          padding:clamp(14px,3vw,22px); margin-bottom:16px;
          transition:box-shadow .2s ease, transform .2s ease;
        }
        .tm-target.flash{ box-shadow:0 0 0 8px var(--ring), var(--shadow); }
        .tm-target h4{ text-align:center; margin:0 0 8px; font-weight:600; color:var(--muted); }
        .tm-target-box{
          width:clamp(110px,30vw,150px); height:clamp(110px,30vw,150px);
          margin:0 auto; display:flex; align-items:center; justify-content:center;
          border-radius:20px; background:#fff; border:2px solid var(--border);
        }
        .tm-bounce{ animation:tm-bounce 1.1s ease-in-out infinite; }
        @keyframes tm-bounce{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }

        .tm-caption{ text-align:center; margin-top:8px; color:var(--muted); font-size:14px; }

        .tm-grid{ display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:16px; }
        @media (min-width:560px){ .tm-grid{ gap:16px; } }

        /* Reduced card height a bit to avoid page overflow on small screens */
        .tm-choice{
          display:flex; align-items:center; justify-content:center;
          height:clamp(96px,24vw,148px);
          font-size:clamp(52px,13vw,82px);
          background:#fff; border:2px solid var(--border);
          border-radius:var(--radius-2xl); box-shadow:var(--shadow);
          transition:transform .08s ease, border-color .15s ease, box-shadow .15s ease, filter .15s ease;
        }
        .tm-choice:active{ transform:scale(0.97); }
        .tm-choice:hover{ border-color:#d7dde6; }
        .tm-choice.disabled{ opacity:.55; filter:grayscale(.2); pointer-events:none; }
        .tm-choice.wrong{ animation:tm-wiggle .28s ease-in-out 1; border-color:#fecaca; }
        @keyframes tm-wiggle{ 0%{transform:rotate(0)}25%{transform:rotate(-5deg)}50%{transform:rotate(5deg)}100%{transform:rotate(0)} }

        .tm-controls{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
        .tm-btn{
          padding:10px 16px; font-weight:700; font-size:16px;
          background:#fff; border:1px solid var(--border); border-radius:14px;
          box-shadow:var(--shadow); cursor:pointer; transition:transform .08s ease, background .15s ease;
        }
        .tm-btn:active{ transform:scale(.97); }
        .tm-btn:hover{ background:#f7fafc; }

        .tm-seg{ display:inline-flex; border:1px solid var(--border); border-radius:14px; overflow:hidden; }
        .tm-seg button{ background:#fff; padding:8px 12px; font-weight:700; border:none; cursor:pointer; }
        .tm-seg button.active{ background:#111827; color:#fff; }

        /* Overlay: fixed to viewport, very high z-index; body scroll is locked in JS */
        .tm-overlay{
          position:fixed; inset:0; background:rgba(2,6,23,.28);
          display:flex; align-items:center; justify-content:center;
          z-index:2147483647; /* above site chrome */
          backdrop-filter:blur(1px);
        }
        .tm-modal{
          background:#fff; border:1px solid var(--border); border-radius:20px;
          box-shadow:0 16px 40px rgba(0,0,0,.25); padding:24px 28px; text-align:center; max-width:320px;
        }
        .tm-modal .emoji{ font-size:56px; margin-bottom:6px; }
      `}</style>

      <div className="tm-shell">
        <div className="tm-row">
          <div className="tm-stars" aria-label="stars">
            <span style={{ fontSize: 22 }}>⭐</span>
            <strong style={{ fontSize: 18 }}>{stars}</strong>
          </div>
          <div className="tm-round">Round {round}</div>
        </div>

        <div className={"tm-target" + (flashTarget ? " flash" : "")}>
          <h4>Find this picture:</h4>
          <div className={"tm-target-box" + (phase === "intro" ? " tm-bounce" : "")} aria-live="polite">
            <span style={{ fontSize: "clamp(64px, 18vw, 92px)" }}>{target?.emoji ?? ""}</span>
          </div>
          <div className="tm-caption">“Where is the {target?.label}? Tap it!”</div>
        </div>

        <div className="tm-grid">
          {options.map((opt) => (
            <button
              key={opt.id}
              aria-label={`choose ${opt.label}`}
              disabled={phase !== "find"}
              onClick={() => handlePick(opt)}
              className={
                "tm-choice" +
                (phase !== "find" ? " disabled" : "") +
                (wrongPickId === opt.id ? " wrong" : "")
              }
            >
              <span>{opt.emoji}</span>
            </button>
          ))}
        </div>

        <div className="tm-controls">
          <button className="tm-btn" onClick={showAgain}>Show again</button>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--muted)", fontSize: 14 }}>Choices:</span>
            <div className="tm-seg">
              {[3, 4].map((n) => (
                <button
                  key={n}
                  className={optionsCount === n ? "active" : ""}
                  onClick={() => setOptionsCount(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <button className="tm-btn" style={{ marginLeft: "auto" }} onClick={startRound}>
            New round
          </button>
        </div>

        {phase === "feedback" && (
          <div className="tm-overlay">
            <div className="tm-modal">
              <div className="emoji">🎉</div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>Great job!</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}